import React, { useContext, useMemo } from 'react'
import HeadData from 'components/Common/HeadData'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'components/Luxkit/Divider'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Places from 'constants/places'
import HeroImage from 'content/components/HeroImage'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GeoContext from 'contexts/geoContext'
import TrendingDestinationsCarousel from 'home/components/TrendingDestinations/TrendingDestinationsCarousel'
import { rem } from 'polished'
import styled from 'styled-components'
import HomesAndVillasVerticalPageUSPs from './HomesAndVillasVerticalPageUSPs'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import { useVillasTrendingDestinations } from 'hooks/Villas/useVillasTrendingDestinations'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import { HOMES_AND_VILLAS_OFFER_TYPES } from 'constants/offerList'
import HomesAndVillasVerticalPageCollections from './HomesAndVillasVerticalPageCollections'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferList from 'hooks/Offers/useOfferList'
import { take, unique } from 'lib/array/arrayUtils'
import { useHomesAndVillasDestinationMaxSavings } from 'home/hooks/useHomesAndVillasDestinationMaxSavings'
import { excludeNullOrUndefined } from 'checkout/utils'
import { VILLAS_TOP_LOCATIONS } from 'constants/villas'
// import LineSwimmerIcon from 'components/Luxkit/Icons/line/LineSwimmerIcon'
// import LineFlowerIcon from 'components/Luxkit/Icons/line/LineFlowerIcon'
// import LineOvenIcon from 'components/Luxkit/Icons/line/LineOvenIcon'

const Section = styled.div`
  padding: ${rem(40)} 0;
`

const headTitle = config.BRAND === LUXURY_ESCAPES ? 'Luxury Homes & Villas at the Best Prices' : 'Homes & Villas'
const description = config.BRAND === LUXURY_ESCAPES ? 'Unwind in our homes & villas. Explore our unique stays, and relish the privacy & exclusivity of these hidden gems.' : undefined

// CONLT-1437: Temporarily remove family friendly chips
const familyFriendlyAmenities = [
  // { label: 'Private pool', value: 'Private pool', icon: <LineSwimmerIcon /> },
  // { label: 'Outdoor space', value: 'Outdoor space', icon: <LineFlowerIcon /> },
  // { label: 'Fully Equipped Kitchen', value: 'Kitchen', icon: <LineOvenIcon /> },
]

function HomeAndVillasVerticalPage() {
  const { currentRegionCode } = useContext(GeoContext)
  const trendingDestinations = useVillasTrendingDestinations()
  const destinationIds = useMemo(() => trendingDestinations.map(destination => destination.destinationId).filter(excludeNullOrUndefined), [trendingDestinations])
  const [maxSavings, fetchingMaxSavings] = useHomesAndVillasDestinationMaxSavings(destinationIds)

  const topFilters = useMemo((): App.OfferListFilters => ({
    offerTypes: HOMES_AND_VILLAS_OFFER_TYPES,
    holidayTypesScoped: ['Homes & Villas'],
    destinations: [Places.Anywhere.id],
  }), [])

  const topTracking = useOfferListTracking(getListName('OfferList', 'VILLAS', 'Top'))
  const topOfferListFilters = useOfferListFilters(topFilters)
  const topAdditionalFilters = useMemo(() => {
    return VILLAS_TOP_LOCATIONS.filter(location => {
      const filter = topOfferListFilters.filters?.locations?.[location.value]
      return !!filter && filter >= 3
    })
  }, [topOfferListFilters.filters?.locations])
  const topOffersList = useOfferList(topFilters)

  const beachfrontFilters = useMemo((): App.OfferListFilters => ({
    offerTypes: HOMES_AND_VILLAS_OFFER_TYPES,
    holidayTypesScoped: ['Homes & Villas'],
    holidayTypes: ['Sun & Beach'],
    destinations: [Places.Anywhere.id],
  }), [])
  const beachfrontExcludedIds = useMemo(() => take(topOffersList.offerIds, 4), [topOffersList])
  const beachfrontOffersList = useOfferList(beachfrontFilters, { excludedIds: beachfrontExcludedIds })

  const beachfrontTracking = useOfferListTracking(getListName('OfferList', 'VILLAS', 'Beach'))

  const familyFriendlyFilters = useMemo((): App.OfferListFilters => ({
    offerTypes: HOMES_AND_VILLAS_OFFER_TYPES,
    holidayTypesScoped: ['Homes & Villas'],
    holidayTypes: ['Family Friendly'],
    destinations: [Places.Anywhere.id],
  }), [])
  const familyFriendlyExcludedIds = useMemo(() => unique([
    ...take(beachfrontOffersList.offerIds, 4),
    ...beachfrontExcludedIds,
  ]), [beachfrontExcludedIds, beachfrontOffersList.offerIds])
  const familyFriendlyOffersList = useOfferList(familyFriendlyFilters, { excludedIds: familyFriendlyExcludedIds })

  const familyFriendlyTracking = useOfferListTracking(getListName('OfferList', 'VILLAS', 'Family friendly'))

  const familyFriendlyOfferListFilters = useOfferListFilters(familyFriendlyFilters)
  const familyFriendlyAdditionalFilters = useMemo(() => {
    return familyFriendlyAmenities.filter(amenity => {
      const filter = familyFriendlyOfferListFilters.filters?.amenities?.[amenity.value]
      return !!filter && filter >= 3
    })
  }, [familyFriendlyOfferListFilters.filters?.amenities])

  const bigGroupFilters = useMemo((): App.OfferListFilters => ({
    offerTypes: HOMES_AND_VILLAS_OFFER_TYPES,
    holidayTypesScoped: ['Homes & Villas'],
    holidayTypes: ['Group'],
    destinations: [Places.Anywhere.id],
  }), [])
  const bigGroupExcludedIds = useMemo(() => unique([
    ...take(familyFriendlyOffersList.offerIds, 4),
    ...familyFriendlyExcludedIds,
  ]), [familyFriendlyExcludedIds, familyFriendlyOffersList.offerIds])

  const bigGroupTracking = useOfferListTracking(getListName('OfferList', 'VILLAS', 'Big group'))
  const bigGroupOfferListFilters = useOfferListFilters(bigGroupFilters)

  const bigGroupAdditionalFilters = useMemo(() => {
    return Object.entries(bigGroupOfferListFilters.filters?.bedrooms || {}).filter(([value, count]) => parseInt(value) >= 3 && count > 0).map(([value]) => ({
      label: `${value} bedrooms`,
      value,
    }))
  }, [bigGroupOfferListFilters.filters?.bedrooms])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage} >
    <Pane>
      <HeadData title={`${headTitle} - ${config.title} ${currentRegionCode}`} description={description} noIndex />
      <HeroImage id="wax661tb18xmibibl2yt" brightness={-35}>
        <VerticalSpacer gap={8} style={{ position: 'relative' }}>
          <Heading variant="heading2" colour="neutral-eight">
            Unwind in our <i>homes & villas</i>
          </Heading>
          <BodyText variant="large" colour="neutral-eight">
            Explore our unique stays, and relish the privacy & exclusivity of these hidden gems.
          </BodyText>
        </VerticalSpacer>
      </HeroImage>
      <Section>
        <HomesAndVillasVerticalPageCollections filters={topFilters} />
      </Section>
      <Section>
        <VerticalSpacer gap={32}>
          <LayoutContainer>
            <Heading variant="heading2">
              Immerse yourself in trending <i>destinations</i>
            </Heading>
          </LayoutContainer>
          <TrendingDestinationsCarousel
            trendingDestinations={trendingDestinations}
            fetching={fetchingMaxSavings}
            maxSavings={maxSavings}
          />
        </VerticalSpacer>
      </Section>
      <OfferListEventsProvider tracking={topTracking}>
        <OfferCardCarouselSection
          title={<><i>Top</i> homes & villas around the world</>}
          filters={topFilters}
          additionalFilters="locations"
          additionalFilterOptions={topAdditionalFilters}
          allLink={`/search/homes-and-villas?${getSearchQueryFromFilter(topFilters)}`}
        />
      </OfferListEventsProvider>
      <LayoutContainer>
        <SupportTile />
      </LayoutContainer>
      <OfferListEventsProvider tracking={beachfrontTracking}>
        <OfferCardCarouselSection
          title={<><i>Beachfront</i> homes</>}
          filters={beachfrontFilters}
          allLink={`/search/homes-and-villas?${getSearchQueryFromFilter(beachfrontFilters)}`}
          excludedIds={beachfrontExcludedIds}
        />
      </OfferListEventsProvider>
      <OfferListEventsProvider tracking={familyFriendlyTracking}>
        <OfferCardCarouselSection
          title={<><i>Family friendly</i> homes</>}
          filters={familyFriendlyFilters}
          additionalFilters="amenities"
          additionalFilterOptions={familyFriendlyAdditionalFilters}
          allLink={`/search/homes-and-villas?${getSearchQueryFromFilter(familyFriendlyFilters)}`}
          excludedIds={familyFriendlyExcludedIds}
        />
      </OfferListEventsProvider>
      <Section>
        <LayoutContainer>
          <Divider direction="horizontal" kind="primary" />
          <Section>
            <VerticalSpacer gap={20}>
              <Heading variant="heading4">What makes our homes & villas special</Heading>
              <HomesAndVillasVerticalPageUSPs />
            </VerticalSpacer>
          </Section>
          <Divider direction="horizontal" kind="primary" />
        </LayoutContainer>
      </Section>
      <OfferListEventsProvider tracking={bigGroupTracking}>
        <OfferCardCarouselSection
          title={<>Perfect for <i>big groups</i></>}
          filters={bigGroupFilters}
          additionalFilters="bedrooms"
          additionalFilterOptions={bigGroupAdditionalFilters}
          allLink={`/search/homes-and-villas?${getSearchQueryFromFilter(bigGroupFilters)}`}
          excludedIds={bigGroupExcludedIds}
        />
      </OfferListEventsProvider>
    </Pane>
  </AnalyticsPageContext.Provider>
}

export default HomeAndVillasVerticalPage
